import { useCallback, useEffect, useRef, useState } from "react";
import { IconButton } from "@/components/common/IconButton";
import { KindFilterChip, Pill } from "@/components/infra/InfraParts";
import {
	captionStyle,
	cellStyle,
	pageTitleStyle,
	sectionHeadingStyle,
} from "@/components/infra/infra-styles";
import {
	getInfraStorage,
	type InfraSharedVolume,
	type InfraStorage,
	type InfraVolumeSnapshot,
} from "@/lib/api";
import { formatBytes } from "@/lib/format-bytes";
import { formatDateTime, formatTime } from "@/lib/format-datetime";
import { toggleKind } from "@/lib/infra-placement";
import {
	INFRA_STORAGE_KINDS,
	INFRA_STORAGE_KIND_LABELS,
	filterStorageByOrganization,
	infraStorageOrganizations,
	isStorageKindShown,
	sharedVolumeStatus,
	volumeSnapshotStatus,
	type InfraStorageKind,
} from "@/lib/infra-storage";

const REFRESH_INTERVAL_MS = 30_000;

/**
 * 管理ページの「ストレージ」タブ (/admin/storage、SuperAdmin のみ)。全組織の共有ボリュームと、
 * 停止中の環境ボリュームの退避スナップショットを定期的に読み直して見せる。
 */
export function StoragePage() {
	const [storage, setStorage] = useState<InfraStorage | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [selectedKinds, setSelectedKinds] = useState<ReadonlySet<InfraStorageKind>>(new Set());
	const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
	const mounted = useRef(true);

	const load = useCallback(() => {
		setLoading(true);
		getInfraStorage()
			.then((data) => {
				if (!mounted.current) return;
				setStorage(data);
				setError(null);
			})
			.catch((err: Error) => {
				if (mounted.current) setError(err.message);
			})
			.finally(() => {
				if (mounted.current) setLoading(false);
			});
	}, []);

	useEffect(() => {
		mounted.current = true;
		load();
		const timer = window.setInterval(load, REFRESH_INTERVAL_MS);
		return () => {
			mounted.current = false;
			window.clearInterval(timer);
		};
	}, [load]);

	const organizations = storage === null ? [] : infraStorageOrganizations(storage);
	// 選んだ組織のストレージが読み直しで無くなったら、全組織の表示に戻す。
	const organizationId = organizations.some((o) => o.id === selectedOrganizationId)
		? selectedOrganizationId
		: null;
	const { sharedVolumes, volumeSnapshots } =
		storage === null
			? { sharedVolumes: [], volumeSnapshots: [] }
			: filterStorageByOrganization(storage, organizationId);
	const kindCounts: Record<InfraStorageKind, number> = {
		sharedVolume: sharedVolumes.length,
		volumeSnapshot: volumeSnapshots.length,
	};

	return (
		<div
			data-testid="storage-page"
			style={{ padding: 32, height: "100%", overflowY: "auto", background: "var(--surface-1)" }}
		>
			<h2 style={pageTitleStyle}>ストレージ</h2>
			<p style={captionStyle({ margin: "6px 0 16px" })}>
				全組織の共有ボリュームと、停止中の環境ボリュームを退避したスナップショットです。
				{REFRESH_INTERVAL_MS / 1000} 秒ごとに読み直します。
				{storage !== null && ` 最終更新: ${formatTime(storage.observedAt)}`}
			</p>

			{error !== null && (
				<p
					data-testid="storage-error"
					style={{ margin: "0 0 16px", fontSize: "var(--text-ui)", color: "var(--danger)" }}
				>
					{error}
				</p>
			)}
			{error === null && storage === null && <p style={captionStyle({ margin: 0 })}>読み込み中…</p>}

			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					gap: 8,
					marginBottom: 24,
				}}
			>
				{storage !== null && (
					<>
						{INFRA_STORAGE_KINDS.map((kind) => (
							<KindFilterChip
								key={kind}
								label={INFRA_STORAGE_KIND_LABELS[kind]}
								testId={`storage-filter-${kind}`}
								value={kindCounts[kind]}
								selected={selectedKinds.has(kind)}
								onClick={() => setSelectedKinds((current) => toggleKind(current, kind))}
							/>
						))}
						<select
							className="ds-select"
							data-testid="storage-organization-filter"
							value={organizationId ?? ""}
							onChange={(e) => setSelectedOrganizationId(e.target.value || null)}
							style={{ width: 240, height: 32, marginLeft: "auto" }}
						>
							<option value="">すべての組織</option>
							{organizations.map((organization) => (
								<option key={organization.id} value={organization.id}>
									{organization.name} ({organization.itemCount})
								</option>
							))}
						</select>
					</>
				)}
				<IconButton
					icon="rotate"
					title="ストレージを更新"
					data-testid="storage-refresh"
					size={28}
					disabled={loading}
					onClick={load}
					style={{ marginLeft: storage === null ? "auto" : undefined }}
				/>
			</div>

			{storage !== null && isStorageKindShown(selectedKinds, "sharedVolume") && (
				<section data-testid="storage-shared-volumes" style={{ marginBottom: 32 }}>
					<h3 style={sectionHeadingStyle}>共有ボリューム</h3>
					{sharedVolumes.length === 0 ? (
						<p style={captionStyle({ margin: 0 })}>共有ボリュームはありません。</p>
					) : (
						<SharedVolumeTable volumes={sharedVolumes} />
					)}
				</section>
			)}

			{storage !== null && isStorageKindShown(selectedKinds, "volumeSnapshot") && (
				<section data-testid="storage-volume-snapshots">
					<h3 style={sectionHeadingStyle}>ボリュームスナップショット</h3>
					{volumeSnapshots.length === 0 ? (
						<p style={captionStyle({ margin: 0 })}>ボリュームスナップショットはありません。</p>
					) : (
						<VolumeSnapshotTable snapshots={volumeSnapshots} />
					)}
				</section>
			)}
		</div>
	);
}

function SharedVolumeTable({ volumes }: { volumes: InfraSharedVolume[] }) {
	return (
		<table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
			<thead>
				<tr style={captionStyle({ textAlign: "left" })}>
					<th style={{ ...cellStyle, fontWeight: 500 }}>ボリューム</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 96 }}>状態</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 160 }}>使用量</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 120 }}>マウント先の環境</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 144 }}>作成日時</th>
				</tr>
			</thead>
			<tbody>
				{volumes.map((volume) => {
					const status = sharedVolumeStatus(volume);
					return (
						<tr key={volume.id} data-testid={`storage-shared-volume-${volume.id}`}>
							<td style={cellStyle}>
								<NameCell
									name={volume.name}
									nameTitle={volume.id}
									owner={volume.organizationName}
									error={volume.provisionError}
								/>
							</td>
							<td style={cellStyle}>
								<Pill label={status.label} tone={status.tone} />
							</td>
							<td style={captionStyle(cellStyle)}>
								{volume.measuredBytes === null ? "未計測" : formatBytes(volume.measuredBytes)}
								{volume.measuredAt !== null && (
									<>
										<br />
										{formatDateTime(volume.measuredAt)} 計測
									</>
								)}
							</td>
							<td style={captionStyle(cellStyle)}>{volume.attachedEnvironmentCount}</td>
							<td style={captionStyle(cellStyle)}>{formatDateTime(volume.createdAt)}</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	);
}

function VolumeSnapshotTable({ snapshots }: { snapshots: InfraVolumeSnapshot[] }) {
	return (
		<table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
			<thead>
				<tr style={captionStyle({ textAlign: "left" })}>
					<th style={{ ...cellStyle, fontWeight: 500 }}>環境</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 96 }}>状態</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 160 }}>復元サイズ</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 144 }}>作成日時</th>
				</tr>
			</thead>
			<tbody>
				{snapshots.map((snapshot) => {
					const status = volumeSnapshotStatus(snapshot);
					const owner = [snapshot.organizationName, snapshot.projectName]
						.filter(Boolean)
						.join(" / ");
					return (
						<tr key={snapshot.name} data-testid={`storage-volume-snapshot-${snapshot.name}`}>
							<td style={cellStyle}>
								<NameCell
									name={snapshot.environmentName ?? snapshot.environmentId ?? snapshot.name}
									nameTitle={snapshot.name}
									owner={owner}
									error={snapshot.readyToUse ? null : snapshot.error}
								/>
							</td>
							<td style={cellStyle}>
								<Pill label={status.label} tone={status.tone} />
							</td>
							<td style={captionStyle(cellStyle)}>
								{snapshot.restoreSizeBytes === null ? "—" : formatBytes(snapshot.restoreSizeBytes)}
							</td>
							<td style={captionStyle(cellStyle)}>
								{snapshot.createdAt === null ? "—" : formatDateTime(snapshot.createdAt)}
							</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	);
}

/** 名前の下に所属 (組織 / プロジェクト) と、あれば失敗の理由を出す。名前の title は ID。 */
function NameCell({
	name,
	nameTitle,
	owner,
	error,
}: {
	name: string;
	nameTitle: string;
	owner: string;
	error: string | null;
}) {
	return (
		<>
			<div
				title={nameTitle}
				style={{
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					fontSize: "var(--text-caption)",
					color: "var(--fg-1)",
				}}
			>
				{name}
			</div>
			{owner !== "" && (
				<div
					title={owner}
					data-tooltip-if-truncated
					style={captionStyle({
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					})}
				>
					{owner}
				</div>
			)}
			{error !== null && (
				<div style={captionStyle({ color: "var(--danger)", marginTop: 2 })}>{error}</div>
			)}
		</>
	);
}
